import React, { useEffect, useState } from 'react';
import { fetchAllCurrencies, getCurrencySymbol } from '../utils/currency';

interface CurrencySelectorProps {
  value: string;
  onChange: (currency: string) => void;
  className?: string;
}

export default function CurrencySelector({ value, onChange, className }: CurrencySelectorProps) {
  const [currencies, setCurrencies] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchAllCurrencies().then((data) => {
      setCurrencies(data);
      setLoading(false);
    });
  }, []);

  const filtered = Object.entries(currencies).filter(([code, name]) => {
    const term = search.toLowerCase();
    return (
      code.toLowerCase().includes(term) ||
      name.toLowerCase().includes(term)
    );
  });

  const selectedSymbol = getCurrencySymbol(value);

  return (
    <div className="relative w-full">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 hover:border-slate-300 transition-all ${className || ''}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-blue-600 font-mono font-black">{selectedSymbol}</span>
          <span>{value}</span>
          {currencies[value] && (
            <span className="text-slate-400 font-normal hidden sm:inline">— {currencies[value]}</span>
          )}
        </div>
        <span className="text-slate-400 text-[10px]">{isOpen ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <input
              type="text"
              placeholder="Search currency code or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* List */}
          <div className="max-h-52 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-xs text-slate-400">
                Loading currencies...
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400">
                No currencies found.
              </div>
            ) : (
              filtered.map(([code, name]) => {
                const symbol = getCurrencySymbol(code);
                const isSelected = code === value;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => {
                      onChange(code);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs transition-all hover:bg-blue-50 ${
                      isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                    }`}
                  >
                    <span className="font-mono font-black text-blue-600 w-6 shrink-0">{symbol}</span>
                    <span className="font-bold w-10 shrink-0">{code}</span>
                    <span className="text-slate-400 truncate">{name}</span>
                    {isSelected && <span className="ml-auto text-blue-600">✓</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Backdrop to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setIsOpen(false); setSearch(''); }}
        />
      )}
    </div>
  );
}